import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { MovelListItem } from '@visualizador/shared';
import { api } from '../api/client.js';
import { TopBar } from '../components/TopBar.js';
import { formatPrazo, prazoStatus } from '../lib/prazo.js';
import { useSenhaAdmin } from '../components/SenhaAdmin.js';

const SEM_ARQUIVO = '(sem cliente)';

function dimsTexto(d: { largura?: number; altura?: number; profundidade?: number }): string {
  if (!d || (!d.largura && !d.altura && !d.profundidade)) return '';
  const n = (v?: number) => (v ? Math.round(v) : '—');
  return `${n(d.largura)} × ${n(d.altura)} × ${n(d.profundidade)} mm`;
}

/** Link para abrir o móvel, levando o arquivo (identidade exata movel_id+arquivo). */
function abrirMovelHref(m: { movel_id: string; arquivo: string }): string {
  const qs = new URLSearchParams({ movel: m.movel_id });
  if (m.arquivo) qs.set('arquivo', m.arquivo);
  return `/v?${qs.toString()}`;
}

interface Cliente {
  arquivo: string; // nome do arquivo do SketchUp = nome do cliente
  moveis: MovelListItem[];
}

export function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Cliente vindo do "Voltar" do viewer — reabre a lista nele (nao no 1o).
  const clientePref = searchParams.get('cliente');
  const [codigo, setCodigo] = useState('');
  const [abrirErro, setAbrirErro] = useState('');

  const [items, setItems] = useState<MovelListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const [buscaCliente, setBuscaCliente] = useState('');
  const [clienteSel, setClienteSel] = useState<string | null>(null);
  const [modalAdmin, pedirAdmin] = useSenhaAdmin();

  // Busca TODOS os moveis (paginado de 100 em 100) para agrupar por cliente.
  const carregarTudo = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const first = await api.listMoveis('', 1, 100);
      let all = first.items;
      const totalPages = Math.ceil((first.total || 0) / 100);
      for (let p = 2; p <= totalPages && p <= 50; p++) {
        const r = await api.listMoveis('', p, 100);
        all = all.concat(r.items);
      }
      setItems(all);
    } catch {
      setErro('Erro ao carregar a lista. O servidor/banco esta rodando?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarTudo();
  }, [carregarTudo]);

  // Agrupa moveis por cliente (= nome do arquivo do SketchUp).
  const clientes = useMemo<Cliente[]>(() => {
    const map = new Map<string, MovelListItem[]>();
    for (const m of items) {
      const key = m.arquivo?.trim() || SEM_ARQUIVO;
      const arr = map.get(key);
      if (arr) arr.push(m);
      else map.set(key, [m]);
    }
    return [...map.entries()]
      .map(([arquivo, moveis]) => ({ arquivo, moveis }))
      .sort((a, b) => a.arquivo.localeCompare(b.arquivo, 'pt', { sensitivity: 'base' }));
  }, [items]);

  // Filtra a lista de clientes pela busca.
  const clientesFiltrados = useMemo(() => {
    const q = buscaCliente.trim().toLowerCase();
    if (!q) return clientes;
    return clientes.filter((c) => c.arquivo.toLowerCase().includes(q));
  }, [clientes, buscaCliente]);

  // Seleciona o cliente: mantem o atual se valido; senao o que veio do "Voltar"
  // (?cliente=); senao o 1o da lista.
  useEffect(() => {
    if (clientes.length === 0) { setClienteSel(null); return; }
    setClienteSel((atual) => {
      if (atual && clientes.some((c) => c.arquivo === atual)) return atual;
      if (clientePref && clientes.some((c) => c.arquivo === clientePref)) return clientePref;
      return clientes[0].arquivo;
    });
  }, [clientes, clientePref]);

  const moveisDoCliente = useMemo(
    () => clientes.find((c) => c.arquivo === clienteSel)?.moveis ?? [],
    [clientes, clienteSel],
  );

  async function abrirCodigo() {
    const code = codigo.trim();
    setAbrirErro('');
    if (!code) return;
    const movel = await api.getMovel(code);
    if (movel) { navigate(`/v?movel=${encodeURIComponent(code)}`); return; }
    const peca = await api.resolvePeca(code);
    if (peca.kind !== 'notfound') { navigate(`/v?peca=${encodeURIComponent(code)}`); return; }
    setAbrirErro(`Nada encontrado para "${code}". Verifique o codigo (movel ou peca).`);
  }

  async function excluir(m: MovelListItem) {
    const nome = m.nome || m.movel_id;
    if (!window.confirm(`Excluir o móvel "${nome}" (${m.movel_id}) do banco?\n\nIsso remove também o modelo 3D e não pode ser desfeito.`)) {
      return;
    }
    let status = await api.deleteMovel(m.movel_id, m.arquivo);
    // Apagar e so do administrador: pede a senha e tenta mais uma vez.
    if ((status === 401 || status === 403) && (await pedirAdmin())) {
      status = await api.deleteMovel(m.movel_id, m.arquivo);
    }
    if (status >= 200 && status < 300) carregarTudo();
    else if (status !== 401 && status !== 403) window.alert('Não foi possível excluir. Tente novamente.');
  }

  return (
    <>
      <TopBar>
        <Link className="btn" to="/scan">📷 Escanear QR</Link>
      </TopBar>

      <div className="container">
        <h1>Abrir um móvel ou peça</h1>
        <p className="sub">Digite o código (ex.: <b>P12345</b>) ou escolha o cliente e o móvel abaixo.</p>

        <div className="card">
          <div className="row">
            <input
              type="text"
              placeholder="Código do móvel ou da peça (ex.: P12345)"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void abrirCodigo(); }}
              autoComplete="off"
            />
            <button className="primary" onClick={() => void abrirCodigo()}>Abrir</button>
          </div>
          {abrirErro && <div className="error-box small" style={{ marginTop: 10 }}>{abrirErro}</div>}
        </div>

        {loading ? (
          <div className="card"><div className="empty">Carregando…</div></div>
        ) : erro ? (
          <div className="card"><div className="error-box">{erro}</div></div>
        ) : items.length === 0 ? (
          <div className="card">
            <div className="empty">Nenhum móvel cadastrado ainda. Publique um móvel pelo plugin Prancha Mania.</div>
          </div>
        ) : (
          <div className="home-2col">
            {/* Coluna 1: lista de clientes (nome do arquivo do SketchUp) */}
            <div className="card home-clientes">
              <h2 className="home-col-tit">Clientes</h2>
              <div className="row" style={{ marginBottom: 12 }}>
                <input
                  type="search"
                  placeholder="Buscar cliente…"
                  value={buscaCliente}
                  onChange={(e) => setBuscaCliente(e.target.value)}
                  autoComplete="off"
                />
              </div>
              {clientesFiltrados.length === 0 ? (
                <div className="empty">Nenhum cliente encontrado.</div>
              ) : (
                <div className="cli-list">
                  {clientesFiltrados.map((c) => (
                    <button
                      key={c.arquivo}
                      type="button"
                      className={`cli-item ${c.arquivo === clienteSel ? 'on' : ''}`}
                      onClick={() => setClienteSel(c.arquivo)}
                    >
                      <span className="cli-nome">{c.arquivo === SEM_ARQUIVO ? '(sem cliente)' : c.arquivo}</span>
                      <span className="cli-count">{c.moveis.length}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Coluna 2: moveis do cliente escolhido */}
            <div className="card home-moveis">
              <div className="home-moveis-head">
                <h2 className="home-col-tit">
                  {clienteSel && clienteSel !== SEM_ARQUIVO ? `Móveis · ${clienteSel}` : 'Móveis'}
                  <span className="muted small"> · {moveisDoCliente.length}</span>
                </h2>
                {clienteSel && clienteSel !== SEM_ARQUIVO && moveisDoCliente.length > 0 && (
                  <Link className="btn primary pj-abrir" to={`/projeto?cliente=${encodeURIComponent(clienteSel)}`}>
                    🏠 Ver todos juntos
                  </Link>
                )}
              </div>
              {moveisDoCliente.length === 0 ? (
                <div className="empty">Selecione um cliente à esquerda.</div>
              ) : (
                <div className="list">
                  {moveisDoCliente.map((m) => (
                    <div
                      key={`${m.movel_id}|${m.arquivo}`}
                      className="item"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(abrirMovelHref(m))}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(abrirMovelHref(m)); }}
                    >
                      <span className="id">{m.movel_id}</span>
                      <div className="meta">
                        <span className="name">{m.nome || '(sem nome)'}</span>
                        <span className="det">{[m.projeto, m.cliente].filter(Boolean).join(' · ') || '—'}</span>
                        <span className="det">
                          {dimsTexto(m.dims) && <>📐 {dimsTexto(m.dims)} · </>}
                          <span className={`prazo-badge ${prazoStatus(m.prazo_em)}`}>
                            {prazoStatus(m.prazo_em) === 'atrasado' ? '⚠ Atrasado · ' : '🗓 Entrega: '}
                            {formatPrazo(m.prazo_em)}
                          </span>
                        </span>
                      </div>
                      <button
                        type="button"
                        className="trash"
                        title="Excluir móvel do banco"
                        aria-label={`Excluir ${m.movel_id}`}
                        onClick={(e) => { e.stopPropagation(); void excluir(m); }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 5v6m4-6v6" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {modalAdmin}

      {/* Celular: atalho fixo no rodape para abrir o projeto montado. */}
      {clienteSel && clienteSel !== SEM_ARQUIVO && moveisDoCliente.length > 0 && (
        <Link className="btn primary home-cta" to={`/projeto?cliente=${encodeURIComponent(clienteSel)}`}>
          🏠 Ver todos juntos · {moveisDoCliente.length} {moveisDoCliente.length === 1 ? 'móvel' : 'móveis'}
        </Link>
      )}
    </>
  );
}
